import {
  PetLocationInformationFragment,
  UpdatePetLocationsMutationFn,
  useUpdatePetLocationsMutation,
} from '@embark/shared-components/src/generated/graphql-client-types';
import { UseFormHandleSubmit, UseFormReturn, useForm } from 'react-hook-form';
import { useEffect, useRef } from 'react';

import { LocationAutocompleteProps } from '@embark/shared-components/src/locationAutocomplete/locationAutocomplete';
import { LocationAutocompleteResult } from '@embark/shared-components/src/locationAutocomplete/locationAutocompleteHooks';
import { showCommonErrorSnackbar } from '@embark/shared-components/src/services/snackbar/snackbar';
import { useFetchGoogleLocationPrediction } from '@embark/shared-components/src/locationAutocomplete/locationAutocompleteHooks';
import { useSnackbar } from 'notistack';

type UsePetLocationFormInputReturn = {
  onChangeLiveAddress: LocationAutocompleteProps['onChange'];
  onChangeFromAddress: LocationAutocompleteProps['onChange'];
  onChangeBornAddress: LocationAutocompleteProps['onChange'];
};

type UsePetLocationProps = {
  toggleModal: () => void;
  locations: PetLocationInformationFragment['locations'];
};

export interface PetLocationForm {
  liveLocation: LocationAutocompleteResult | null;
  bornLocation: LocationAutocompleteResult | null;
  fromLocation: LocationAutocompleteResult | null;
}

type UsePetLocationReturn = {
  formInformation: UseFormReturn<PetLocationForm, object>;
  handleSubmit: UseFormHandleSubmit<PetLocationForm>;
  handleClose: () => void;
  placesServiceRef: React.RefObject<HTMLDivElement>;
  updatePetLocationsMutation: UpdatePetLocationsMutationFn;
  onChangeLiveAddress: LocationAutocompleteProps['onChange'];
  onChangeFromAddress: LocationAutocompleteProps['onChange'];
  onChangeBornAddress: LocationAutocompleteProps['onChange'];
};

export function usePetLocationModal(props: UsePetLocationProps): UsePetLocationReturn {
  const { toggleModal, locations } = props;
  const { enqueueSnackbar } = useSnackbar();

  const { data: liveOptions } = useFetchGoogleLocationPrediction(locations.live?.addressInput ?? undefined);
  const { data: fromOptions } = useFetchGoogleLocationPrediction(locations.from?.addressInput ?? undefined);
  const { data: bornOptions } = useFetchGoogleLocationPrediction(locations.born?.addressInput ?? undefined);

  const [updatePetLocationsMutation, {}] = useUpdatePetLocationsMutation({
    onError: () => {
      showCommonErrorSnackbar(enqueueSnackbar);
    },
    onCompleted: () => {
      toggleModal();
      location.reload();
    },
  });
  const placesServiceRef = useRef<HTMLDivElement>(null);

  const handleClose = async (): Promise<void> => {
    toggleModal();
  };
  const formInformation = useForm<PetLocationForm>({
    mode: 'onChange',
  });

  const { setValue, handleSubmit } = formInformation;

  useEffect(() => {
    if (!!liveOptions) {
      setValue('liveLocation', liveOptions[0]);
    }
    if (!!fromOptions) {
      setValue('fromLocation', fromOptions[0]);
    }
    if (!!bornOptions) {
      setValue('bornLocation', bornOptions[0]);
    }
  }, [liveOptions, fromOptions, bornOptions, setValue]);

  function useAddressFormInput(): UsePetLocationFormInputReturn {
    const onChangeLiveAddress: LocationAutocompleteProps['onChange'] = (_event, newValue): void => {
      if (newValue) {
        setValue('liveLocation', newValue);
      }
    };

    const onChangeFromAddress: LocationAutocompleteProps['onChange'] = (_event, newValue): void => {
      if (newValue) {
        setValue('fromLocation', newValue);
      }
    };

    const onChangeBornAddress: LocationAutocompleteProps['onChange'] = (_event, newValue): void => {
      if (newValue) {
        setValue('liveLocation', newValue);
      }
    };

    return {
      onChangeLiveAddress,
      onChangeFromAddress,
      onChangeBornAddress,
    };
  }

  const { onChangeLiveAddress, onChangeFromAddress, onChangeBornAddress } = useAddressFormInput();

  return {
    formInformation,
    handleSubmit,
    handleClose,
    placesServiceRef,
    updatePetLocationsMutation,
    onChangeLiveAddress,
    onChangeFromAddress,
    onChangeBornAddress,
  };
}
